import { createElement, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from 'react'
import { LoadingOutlined } from '@ant-design/icons'
import WarningIcon from '@/assets/warning.svg?react'

export type BrandButtonVariant = 'primary' | 'outline' | 'danger'

/** Figma 确认弹窗感叹号：负向橙 / 正向蓝 */
export const modalWarningTone = {
  danger: '#F77234',
  primary: '#6366F1',
} as const

/** 给 antd Modal.confirm / Button 挂同一套视觉时用 */
export const brandBtnClass = {
  primary: 'brand-btn brand-btn--primary',
  outline: 'brand-btn brand-btn--outline',
  danger: 'brand-btn brand-btn--danger',
} as const

/** Figma 感叹号（28px）；挂 anticon 以吃到 confirm 图标边距 */
export function brandModalIcon(ok: 'primary' | 'danger' = 'primary') {
  return createElement(WarningIcon, {
    className: 'anticon size-7 shrink-0',
    style: { color: modalWarningTone[ok] },
  })
}

/** antd Modal / modal.confirm 的确认+取消按钮 class */
export function brandModalBtnProps(ok: 'primary' | 'danger' = 'primary') {
  return {
    okButtonProps: { className: brandBtnClass[ok] },
    cancelButtonProps: { className: brandBtnClass.outline },
  }
}

/** modal.confirm：品牌按钮 + Figma 感叹号（负向橙 / 正向蓝） */
export function brandModalConfirmProps(ok: 'primary' | 'danger' = 'primary') {
  return {
    icon: brandModalIcon(ok),
    ...brandModalBtnProps(ok),
  }
}

type BrandButtonProps = {
  children: ReactNode
  /** primary 主题 / outline 取消 / danger 删除确认 */
  variant?: BrandButtonVariant
  /** 左侧图标（可选） */
  icon?: ReactNode
  loading?: boolean
  className?: string
  style?: CSSProperties
} & Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'style' | 'className'
>

/**
 * HiBro 操作按钮（对齐 Figma button）
 * - primary：indigo 0.9 + 玻璃描边
 * - outline：浅底 + #DCDCDC 描边（取消等）
 * - danger：红 0.9 + 玻璃描边（删除/下架确认等）
 */
export default function BrandButton({
  children,
  variant = 'primary',
  icon,
  loading,
  disabled,
  className,
  style,
  type = 'button',
  ...rest
}: BrandButtonProps) {
  const blocked = disabled || loading
  const variantClass = brandBtnClass[variant]
  const mergedClass = className ? `${variantClass} ${className}` : variantClass
  const stateStyle = disabled ? disabledStyle : loading ? loadingStyle : null

  return (
    <button
      type={type}
      disabled={blocked}
      className={mergedClass}
      style={{ ...stateStyle, ...style }}
      {...rest}
    >
      {loading ? (
        <span style={iconWrap}>
          <LoadingOutlined spin />
        </span>
      ) : icon ? (
        <span style={iconWrap}>{icon}</span>
      ) : null}
      <span>{children}</span>
    </button>
  )
}

const disabledStyle: CSSProperties = {
  opacity: 0.45,
  cursor: 'not-allowed',
}

const loadingStyle: CSSProperties = {
  opacity: 0.65,
  cursor: 'default',
}

const iconWrap: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}
